import React, { useState, useEffect } from 'react';

function LandingPage() {
  // Responsive hook
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  // Stripe Payment Links
  const PASS_ONLY_LINK = 'https://buy.stripe.com/5kQdRa2ix41Z7m54yJ4wM0a';
  const BUNDLE_LINK = 'https://buy.stripe.com/dRmdRa3mB2XVayh9T34wM0b';

  // The Master Your PASS Profile course is off sale while it is rewritten for the
  // redesigned report (Ted, 2026-10-14). Set to true to show the bundle card again.
  const COURSE_ON_SALE = false;

  // The course bundle has no Payment Link — the checkout is built server-side by
  // create-course-bundle-checkout.js so the product_type metadata is always correct.
  const [courseCheckoutLoading, setCourseCheckoutLoading] = useState(false);
  const [courseCheckoutError, setCourseCheckoutError] = useState('');

  const handlePurchase = () => {
    window.location.href = PASS_ONLY_LINK;
  };

  const handleBundlePurchase = () => {
    window.location.href = BUNDLE_LINK;
  };

  const handleCourseBundlePurchase = async () => {
    setCourseCheckoutLoading(true);
    setCourseCheckoutError('');
    try {
      const response = await fetch('/.netlify/functions/create-course-bundle-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await response.json();

      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
        return; // leaving the page — keep the button in its loading state
      }
      throw new Error(data.error || 'Could not start checkout');
    } catch (err) {
      console.error('Course bundle checkout failed:', err);
      setCourseCheckoutError("Sorry, checkout couldn't start. Please try again.");
      setCourseCheckoutLoading(false);
    }
  };

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  // PASS Brand Colors
  const colors = {
    deepForestGreen: '#0B3D2E',
    teal: '#2A9D8F',
    white: '#FFFFFF',
    lightBg: '#f8faf9',
    textPrimary: '#333333',
    textSecondary: '#666666'
  };

  const styles = {
    // Page Container
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: colors.textPrimary,
      lineHeight: 1.6
    },

    // Header
    header: {
      backgroundColor: colors.white,
      borderBottom: `3px solid ${colors.teal}`,
      padding: isMobile ? '12px 16px' : '16px 24px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    },
    headerContainer: {
      maxWidth: '1100px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    logo: {
      height: isMobile ? '40px' : '48px'
    },
    navLink: {
      color: colors.deepForestGreen,
      textDecoration: 'none',
      fontWeight: 600,
      padding: '8px 20px',
      borderRadius: '6px',
      border: `2px solid ${colors.deepForestGreen}`,
      transition: 'all 0.2s ease'
    },

    // Hero Section
    hero: {
      background: `linear-gradient(135deg, ${colors.deepForestGreen} 0%, #145c47 100%)`,
      color: colors.white,
      padding: isMobile ? '40px 16px 60px' : '60px 24px 80px',
      position: 'relative'
    },
    heroContainer: {
      maxWidth: '1100px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
      gap: isMobile ? '24px' : '40px',
      alignItems: 'center'
    },
    heroContent: {
      zIndex: 2,
      textAlign: isMobile ? 'center' : 'left'
    },
    heroTitle: {
      fontSize: isMobile ? '2rem' : isTablet ? '2.25rem' : '2.75rem',
      fontWeight: 700,
      marginBottom: '20px',
      lineHeight: 1.2
    },
    heroSubtitle: {
      fontSize: isMobile ? '1rem' : '1.15rem',
      marginBottom: '28px',
      opacity: 0.95,
      lineHeight: 1.7
    },
    heroVisual: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      order: isMobile ? 1 : 0
    },
    heroImage: {
      maxWidth: isMobile ? '200px' : '300px',
      width: '100%',
      height: 'auto',
      filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.3))'
    },
    
    // CTA Button
    ctaButton: {
      backgroundColor: colors.teal,
      color: colors.white,
      border: 'none',
      padding: '18px 40px',
      fontSize: '1.2rem',
      fontWeight: 600,
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 14px rgba(42, 157, 143, 0.4)'
    },
    ctaButtonSecondary: {
      backgroundColor: colors.white,
      color: colors.deepForestGreen,
      border: 'none',
      padding: '18px 40px',
      fontSize: '1.2rem',
      fontWeight: 600,
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
    },

    // Value Prop Section
    valueProp: {
      backgroundColor: colors.white,
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    valuePropContainer: {
      maxWidth: '1000px',
      margin: '0 auto',
      textAlign: 'center'
    },
    sectionTitle: {
      fontSize: isMobile ? '1.75rem' : '2.25rem',
      fontWeight: 700,
      color: colors.deepForestGreen,
      marginBottom: '16px'
    },
    sectionSubtitle: {
      fontSize: '1.15rem',
      color: colors.textSecondary,
      marginBottom: '48px',
      maxWidth: '700px',
      margin: '0 auto 48px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '24px' : '40px',
      marginTop: '48px'
    },
    statItem: {
      textAlign: 'center'
    },
    statNumber: {
      fontSize: '3rem',
      fontWeight: 700,
      color: colors.teal,
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: isMobile ? '0.85rem' : '1rem',
      color: colors.textSecondary
    },

    // Trust Bar
    trustBar: {
      backgroundColor: colors.white,
      padding: isMobile ? '32px 16px' : '48px 24px',
      borderTop: '1px solid #E8ECF0',
      borderBottom: '1px solid #E8ECF0'
    },
    trustBarContainer: {
      maxWidth: '1000px',
      margin: '0 auto'
    },
    trustBarGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '24px' : '40px'
    },
    trustStat: {
      textAlign: 'center'
    },
    trustNumber: {
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: 700,
      color: colors.teal,
      marginBottom: '4px',
      lineHeight: 1.2
    },
    trustLabel: {
      fontSize: isMobile ? '0.8rem' : '0.9rem',
      color: colors.textSecondary,
      lineHeight: 1.4
    },

    // Differentiator Section
    differentiator: {
      backgroundColor: colors.lightBg,
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    differentiatorContainer: {
      maxWidth: '1100px',
      margin: '0 auto',
      textAlign: 'center'
    },
    differentiatorGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? '20px' : '28px',
      marginTop: isMobile ? '32px' : '48px',
      textAlign: 'left'
    },
    differentiatorCard: {
      backgroundColor: colors.white,
      padding: isMobile ? '24px' : '32px',
      borderRadius: '12px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      display: 'flex',
      gap: '16px',
      alignItems: 'flex-start'
    },
    differentiatorIcon: {
      width: '48px',
      height: '48px',
      minWidth: '48px',
      backgroundColor: `${colors.teal}12`,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    differentiatorContent: {
      flex: 1
    },
    differentiatorTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: colors.deepForestGreen,
      marginBottom: '8px'
    },
    differentiatorBody: {
      fontSize: '0.95rem',
      color: colors.textSecondary,
      lineHeight: 1.6
    },

    // Report Contents Section
    reportContents: {
      backgroundColor: colors.white,
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    reportContentsContainer: {
      maxWidth: '1100px',
      margin: '0 auto',
      textAlign: 'center'
    },
    reportContentsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? '16px' : '20px',
      marginTop: isMobile ? '32px' : '48px',
      textAlign: 'left'
    },
    reportContentCard: {
      display: 'flex',
      gap: '16px',
      alignItems: 'flex-start',
      padding: isMobile ? '16px' : '20px 24px',
      borderLeft: `4px solid ${colors.teal}`,
      backgroundColor: '#f9fbfc',
      borderRadius: '0 8px 8px 0'
    },
    reportContentIcon: {
      width: '40px',
      height: '40px',
      minWidth: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    reportContentInfo: {
      flex: 1
    },
    reportContentTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.deepForestGreen,
      marginBottom: '4px'
    },
    reportContentDesc: {
      fontSize: '0.9rem',
      color: colors.textSecondary,
      lineHeight: 1.5
    },

    // Features Section
    features: {
      backgroundColor: colors.lightBg,
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    featuresContainer: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '20px' : '32px',
      marginTop: isMobile ? '32px' : '48px'
    },
    featureCard: {
      backgroundColor: colors.white,
      padding: '32px',
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      textAlign: 'center'
    },
    featureIcon: {
      width: '64px',
      height: '64px',
      margin: '0 auto 20px',
      backgroundColor: `${colors.teal}15`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    featureTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: colors.deepForestGreen,
      marginBottom: '12px'
    },
    featureDescription: {
      fontSize: '0.95rem',
      color: colors.textSecondary,
      lineHeight: 1.6
    },

    // How It Works Section
    howItWorks: {
      backgroundColor: colors.white,
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    howItWorksContainer: {
      maxWidth: '900px',
      margin: '0 auto',
      textAlign: 'center'
    },
    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '32px' : '40px',
      marginTop: isMobile ? '32px' : '48px',
      position: 'relative'
    },
    step: {
      textAlign: 'center',
      position: 'relative'
    },
    stepNumber: {
      width: '56px',
      height: '56px',
      backgroundColor: colors.deepForestGreen,
      color: colors.white,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      fontWeight: 700,
      margin: '0 auto 20px'
    },
    stepTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: colors.deepForestGreen,
      marginBottom: '10px'
    },
    stepDescription: {
      fontSize: '0.95rem',
      color: colors.textSecondary,
      lineHeight: 1.5
    },

    // Pricing Section
    pricing: {
      backgroundColor: colors.white,
      padding: isMobile ? '48px 16px' : '80px 24px',
    },
    pricingContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      textAlign: 'center',
    },
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
      gap: '32px',
      marginTop: '48px',
      maxWidth: isMobile ? '780px' : (isTablet ? '780px' : '1140px'),
      margin: '48px auto 0',
    },
    pricingCard: {
      backgroundColor: colors.white,
      borderRadius: '12px',
      padding: '40px 32px',
      border: `2px solid #E8ECF0`,
      textAlign: 'center',
      position: 'relative',
      transition: 'all 0.2s ease',
    },
    pricingCardFeatured: {
      border: `2px solid ${colors.teal}`,
      boxShadow: '0 8px 24px rgba(42, 157, 143, 0.15)',
    },
    pricingBadge: {
      position: 'absolute',
      top: '-14px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: colors.teal,
      color: colors.white,
      padding: '4px 20px',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: 700,
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    },
    pricingCardTitle: {
      fontSize: '1.35rem',
      fontWeight: 700,
      color: colors.deepForestGreen,
      marginBottom: '8px',
    },
    pricingPrice: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: colors.deepForestGreen,
      margin: '16px 0 8px',
    },
    pricingPriceNote: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginBottom: '24px',
    },
    pricingFeatureList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 28px 0',
      textAlign: 'left',
    },
    pricingFeatureItem: {
      fontSize: '0.95rem',
      color: colors.textPrimary,
      padding: '6px 0',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      lineHeight: 1.5,
    },
    pricingCheck: {
      color: colors.teal,
      fontWeight: 700,
      fontSize: '1.1rem',
      flexShrink: 0,
      marginTop: '1px',
    },
    pricingError: {
      marginTop: '12px',
      marginBottom: 0,
      color: '#b00020',
      fontSize: '0.9rem',
    },
    pricingBtn: {
      display: 'inline-block',
      width: '100%',
      padding: '14px 24px',
      fontSize: '1.05rem',
      fontWeight: 600,
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      textAlign: 'center',
    },
    pricingBtnPrimary: {
      backgroundColor: colors.teal,
      color: colors.white,
      boxShadow: '0 4px 14px rgba(42, 157, 143, 0.4)',
    },
    pricingBtnSecondary: {
      backgroundColor: 'transparent',
      color: colors.deepForestGreen,
      border: `2px solid ${colors.deepForestGreen}`,
    },

    // Go Deeper: Voice-Brand Section
    goDeeper: {
      backgroundColor: '#f0faf8',
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    goDeeperContainer: {
      maxWidth: '1000px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr',
      gap: isMobile ? '24px' : '48px',
      alignItems: 'center'
    },
    goDeeperContent: {
      textAlign: isMobile ? 'center' : 'left'
    },
    goDeeperLabel: {
      fontSize: '0.8rem',
      fontWeight: 700,
      color: colors.teal,
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      marginBottom: '8px'
    },
    goDeeperTitle: {
      fontSize: isMobile ? '1.5rem' : '1.75rem',
      fontWeight: 700,
      color: colors.deepForestGreen,
      marginBottom: '16px',
      lineHeight: 1.3
    },
    goDeeperText: {
      fontSize: '1rem',
      color: colors.textPrimary,
      lineHeight: 1.7,
      marginBottom: '20px'
    },
    goDeeperList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 24px 0',
      textAlign: 'left'
    },
    goDeeperListItem: {
      fontSize: '0.95rem',
      color: colors.textPrimary,
      padding: '5px 0',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      lineHeight: 1.5
    },
    goDeeperCheck: {
      color: colors.teal,
      fontWeight: 700,
      fontSize: '1rem',
      flexShrink: 0,
      marginTop: '2px'
    },
    goDeeperVisual: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    goDeeperImage: {
      maxWidth: isMobile ? '200px' : '280px',
      width: '100%',
      height: 'auto',
      borderRadius: '12px',
      filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.1))'
    },
    goDeeperCta: {
      display: 'inline-block',
      backgroundColor: colors.teal,
      color: colors.white,
      border: 'none',
      padding: '14px 32px',
      fontSize: '1.05rem',
      fontWeight: 600,
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 14px rgba(42, 157, 143, 0.3)'
    },
    goDeeperNote: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginTop: '12px'
    },

    // Go Deeper: Team Dynamics Section
    teamDynamics: {
      backgroundColor: colors.white,
      padding: isMobile ? '48px 16px' : '80px 24px'
    },
    teamDynamicsContainer: {
      maxWidth: '1000px',
      margin: '0 auto',
      textAlign: 'center'
    },
    teamDynamicsContent: {
      maxWidth: '700px',
      margin: '0 auto',
      marginBottom: '40px'
    },
    tierGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '16px' : '24px',
      maxWidth: '700px',
      margin: '0 auto 32px'
    },
    tierCard: {
      backgroundColor: colors.lightBg,
      borderRadius: '12px',
      padding: '24px 20px',
      border: '1px solid #E8ECF0',
      textAlign: 'center'
    },
    tierName: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: colors.deepForestGreen,
      marginBottom: '4px'
    },
    tierSize: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginBottom: '12px'
    },
    tierPrice: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: colors.teal
    },
    teamDynamicsCta: {
      display: 'inline-block',
      backgroundColor: 'transparent',
      color: colors.deepForestGreen,
      border: `2px solid ${colors.deepForestGreen}`,
      padding: '14px 32px',
      fontSize: '1.05rem',
      fontWeight: 600,
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none'
    },
    teamDynamicsNote: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginTop: '16px'
    },

    // Final CTA Section
    finalCta: {
      background: `linear-gradient(135deg, ${colors.teal} 0%, #238b7e 100%)`,
      color: colors.white,
      padding: isMobile ? '48px 16px' : '80px 24px',
      textAlign: 'center'
    },
    finalCtaContainer: {
      maxWidth: '700px',
      margin: '0 auto'
    },
    finalCtaTitle: {
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: 700,
      marginBottom: '16px'
    },
    finalCtaSubtitle: {
      fontSize: '1.1rem',
      marginBottom: '32px',
      opacity: 0.95
    },
    guarantee: {
      marginTop: '20px',
      fontSize: '0.95rem',
      opacity: 0.9
    },

    // Footer
    footer: {
      backgroundColor: colors.deepForestGreen,
      color: colors.white,
      padding: '40px 24px',
      textAlign: 'center'
    },
    footerContent: {
      maxWidth: '1100px',
      margin: '0 auto'
    },
    footerText: {
      fontSize: '0.9rem',
      opacity: 0.8,
      marginBottom: '16px'
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      gap: '32px',
      flexWrap: 'wrap'
    },
    footerLink: {
      color: colors.white,
      textDecoration: 'none',
      fontSize: '0.9rem',
      opacity: 0.8,
      transition: 'opacity 0.2s ease'
    }
  };

  // Feature icons as simple SVGs
  const FeatureIcon = ({ type }) => {
    const iconColor = colors.teal;
    const icons = {
      purpose: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
          <circle cx="12" cy="12" r="2"/>
        </svg>
      ),
      thinking: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
          <path d="M12 2a8 8 0 0 1 8 8c0 3.5-2 6-4 7.5V19a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-1.5C6 16 4 13.5 4 10a8 8 0 0 1 8-8z"/>
          <line x1="9" y1="22" x2="15" y2="22"/>
        </svg>
      ),
      relational: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      temporal: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      social: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ),
      adaptability: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
          <polyline points="23 4 23 10 17 10"/>
          <polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
      )
    };
    return icons[type] || icons.purpose;
  };

  // Differentiator icons
  const DiffIcon = ({ type }) => {
    const ic = colors.teal;
    const icons = {
      scenarios: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4"/>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
      ),
      spectrum: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"/>
          <circle cx="6" cy="12" r="2" fill={`${ic}30`}/>
          <circle cx="12" cy="12" r="2" fill={`${ic}60`}/>
          <circle cx="18" cy="12" r="2" fill={ic}/>
        </svg>
      ),
      shield: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <line x1="9" y1="12" x2="15" y2="12"/>
          <line x1="12" y1="9" x2="12" y2="15"/>
        </svg>
      ),
      grid: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
        </svg>
      )
    };
    return icons[type] || icons.scenarios;
  };

  // Report contents icons
  const ReportIcon = ({ type }) => {
    const ic = colors.teal;
    const icons = {
      radar: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2" fill={`${ic}10`}/>
          <polygon points="12 6 18 9.5 18 14.5 12 18 6 14.5 6 9.5 12 6" fill={`${ic}20`}/>
          <circle cx="12" cy="12" r="2" fill={ic}/>
        </svg>
      ),
      trophy: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
          <path d="M4 22h16"/>
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/>
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/>
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
        </svg>
      ),
      book: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          <line x1="8" y1="7" x2="16" y2="7"/>
          <line x1="8" y1="11" x2="13" y2="11"/>
        </svg>
      ),
      briefcase: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      ),
      lightning: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
      ),
      people: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      roadmap: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="5" r="3"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <path d="M6 20l6-4 6 4"/>
        </svg>
      ),
      card: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <line x1="7" y1="8" x2="17" y2="8"/>
          <line x1="7" y1="12" x2="14" y2="12"/>
          <line x1="7" y1="16" x2="11" y2="16"/>
        </svg>
      )
    };
    return icons[type] || icons.radar;
  };

  const reportItems = [
    { icon: 'radar', title: 'Your Communication Fingerprint', desc: 'A visual map of all 40 communication approaches across 6 areas, showing which ones your answers favored.' },
    { icon: 'trophy', title: 'The Approaches You Favor Most', desc: 'The approaches your answers favored most, each with an example from your own answers and a practical way to use it.' },
    { icon: 'book', title: '6 Area Pages', desc: 'Each area of communication gets a clear chart, a plain explanation of your answers, and one practical step.' },
    { icon: 'briefcase', title: 'Where Your Approaches Show Up at Work', desc: 'Work roles across many fields, from skilled trades and health care to the arts and public service, matched to the approaches your answers favored.' },
    { icon: 'radar', title: 'How Strong Each Result Is', desc: 'Each result is checked against random ratings, so you can see which results are clear and which are ideas to try.' },
    { icon: 'book', title: 'How Your Approaches Change by Situation', desc: 'How your ratings shifted with the people you were talking with and the type of situation, with examples from your own answers.' },
    { icon: 'lightning', title: 'Real Conversations', desc: 'Common work conversations with words you could try and a way to prepare for each one.' },
    { icon: 'people', title: 'Working With [Your Name]', desc: 'A short page you can share with colleagues, or copy from your portal and adjust in your own words.' },
    { icon: 'roadmap', title: 'Development Roadmap', desc: 'A simple plan for using the approaches you already favor more deliberately, with feedback questions to ask.' },
    { icon: 'people', title: 'Your Style and Your Voice', desc: 'What PASS describes about your speaking style, and how a voice coach can help with pacing, projection, and the rest of your voice.' },
    { icon: 'card', title: 'Quick Reference', desc: 'A one-page summary of your approaches, words you could use, and questions to ask for feedback.' }
  ];

  const features = [
    {
      type: 'purpose',
      title: 'Communication Purpose',
      description: 'See what you tend to focus on in conversations: the people involved, the result, the shared work, or your own point of view.'
    },
    {
      type: 'thinking',
      title: 'Thinking Style',
      description: 'See how you organize and explain ideas: the big picture first, patterns and connections, careful analysis, or a clear step-by-step order.'
    },
    {
      type: 'relational',
      title: 'Relational Approach',
      description: 'See how you handle the people side of work: listening closely, raising concerns with tact, encouraging others, and speaking directly.'
    },
    {
      type: 'temporal',
      title: 'Planning and Decisions',
      description: 'See how you plan and decide: mapping out priorities, gathering information, pausing to reflect, or moving forward quickly.'
    },
    {
      type: 'social',
      title: 'Role in Groups',
      description: 'See the role you tend to take with a group: involving others, guiding the discussion, taking the lead, or supporting someone else\'s lead.'
    },
    {
      type: 'adaptability',
      title: 'Adaptability',
      description: 'See how you adjust your approach: choosing the right moment, fitting your message to the listener, changing course, or keeping a steady plan.'
    }
  ];

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContainer}>
          <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          <a href="/login" style={styles.navLink}>Login</a>
        </div>
      </header>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContainer}>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>Discover Your Communication Fingerprint</h1>
            <p style={styles.heroSubtitle}>
              Your communication style is as individual as your fingerprint. PASS Profile looks at
              how you respond to 60 real workplace situations and shows which of 40 communication
              approaches you favor, so you can use them with confidence.
            </p>
            <button onClick={scrollToPricing} style={styles.ctaButton}>
              See Pricing Options
            </button>
          </div>
          <div style={styles.heroVisual}>
            <img src="/pass-bubble.png" alt="Communication Fingerprint" style={styles.heroImage} />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section style={styles.trustBar}>
        <div style={styles.trustBarContainer}>
          <div style={styles.trustBarGrid}>
            <div style={styles.trustStat}>
              <div style={styles.trustNumber}>40</div>
              <div style={styles.trustLabel}>Communication Approaches</div>
            </div>
            <div style={styles.trustStat}>
              <div style={styles.trustNumber}>60</div>
              <div style={styles.trustLabel}>Workplace Scenarios</div>
            </div>
            <div style={styles.trustStat}>
              <div style={styles.trustNumber}>10<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>168</sup></div>
              <div style={styles.trustLabel}>Unique Combinations</div>
            </div>
            <div style={styles.trustStat}>
              <div style={styles.trustNumber}>240</div>
              <div style={styles.trustLabel}>Data Points Analyzed</div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes PASS Different */}
      <section style={styles.differentiator}>
        <div style={styles.differentiatorContainer}>
          <h2 style={styles.sectionTitle}>A Communication Fingerprint, Not a Personality Box</h2>
          <p style={styles.sectionSubtitle}>
            Most assessments sort you into a type. PASS Profile does something different.
          </p>
          <div style={styles.differentiatorGrid}>
            <div style={styles.differentiatorCard}>
              <div style={styles.differentiatorIcon}>
                <DiffIcon type="scenarios" />
              </div>
              <div style={styles.differentiatorContent}>
                <div style={styles.differentiatorTitle}>Built on Real Workplace Situations</div>
                <p style={styles.differentiatorBody}>
                  You rate four possible responses to each of 60 workplace situations. Your ratings
                  show how you tend to communicate at work, with examples from your own answers.
                </p>
              </div>
            </div>
            <div style={styles.differentiatorCard}>
              <div style={styles.differentiatorIcon}>
                <DiffIcon type="spectrum" />
              </div>
              <div style={styles.differentiatorContent}>
                <div style={styles.differentiatorTitle}>Every Approach, In Detail</div>
                <p style={styles.differentiatorBody}>
                  Each of the 40 approaches is shown on its own, based on your own answers, so you can
                  see the ones you tend to prefer in full detail, with room for nuance.
                </p>
              </div>
            </div>
            <div style={styles.differentiatorCard}>
              <div style={styles.differentiatorIcon}>
                <DiffIcon type="shield" />
              </div>
              <div style={styles.differentiatorContent}>
                <div style={styles.differentiatorTitle}>100% Asset-Based</div>
                <p style={styles.differentiatorBody}>
                  Your report describes the approaches your answers favored and shows how to
                  build on them. Every approach is treated as a valuable option you can choose on purpose.
                </p>
              </div>
            </div>
            <div style={styles.differentiatorCard}>
              <div style={styles.differentiatorIcon}>
                <DiffIcon type="grid" />
              </div>
              <div style={styles.differentiatorContent}>
                <div style={styles.differentiatorTitle}>40 Approaches in 6 Areas</div>
                <p style={styles.differentiatorBody}>
                  PASS describes 40 distinct communication approaches, so your profile reflects
                  the details of how you communicate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside Your Report */}
      <section style={styles.reportContents}>
        <div style={styles.reportContentsContainer}>
          <h2 style={styles.sectionTitle}>What's Inside Your Report</h2>
          <p style={styles.sectionSubtitle}>
            Every section is built from your own answers to the 60 situations.
          </p>
          <div style={styles.reportContentsGrid}>
            {reportItems.map((item, index) => (
              <div key={index} style={styles.reportContentCard}>
                <div style={styles.reportContentIcon}>
                  <ReportIcon type={item.icon} />
                </div>
                <div style={styles.reportContentInfo}>
                  <div style={styles.reportContentTitle}>{item.title}</div>
                  <p style={styles.reportContentDesc}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={styles.features}>
        <div style={styles.featuresContainer}>
          <h2 style={{...styles.sectionTitle, textAlign: 'center'}}>What You'll Discover</h2>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div key={index} style={styles.featureCard}>
                <div style={styles.featureIcon}>
                  <FeatureIcon type={feature.type} />
                </div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.howItWorks}>
        <div style={styles.howItWorksContainer}>
          <h2 style={styles.sectionTitle}>How It Works</h2>
          <div style={styles.stepsGrid}>
            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <h3 style={styles.stepTitle}>Purchase & Create Your Account</h3>
              <p style={styles.stepDescription}>Secure checkout through Stripe. Your account is ready in seconds.</p>
            </div>
            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <h3 style={styles.stepTitle}>Respond to 60 Workplace Scenarios</h3>
              <p style={styles.stepDescription}>Rate how you'd handle real work situations. Takes about 45 minutes. Save your progress and come back anytime.</p>
            </div>
            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <h3 style={styles.stepTitle}>Receive Your Personalized Report</h3>
              <p style={styles.stepDescription}>Get a personalized PDF report by email, built from your own answers, with practical ways to use what you learn right away.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={styles.pricing}>
        <div style={styles.pricingContainer}>
          <h2 style={styles.sectionTitle}>Choose Your Package</h2>
          <p style={styles.sectionSubtitle}>
            Get your full communication fingerprint, or go deeper with a personalized
            Voice-Brand Alignment report for your role{COURSE_ON_SALE ? ', or the course that teaches you how to read and use your results' : ''}.
          </p>
          <div style={styles.pricingGrid}>

            {/* PASS Only Card */}
            <div style={styles.pricingCard}>
              <h3 style={styles.pricingCardTitle}>PASS Profile</h3>
              <div style={styles.pricingPrice}>$49.99</div>
              <p style={styles.pricingPriceNote}>One-time payment</p>
              <ul style={styles.pricingFeatureList}>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>60 real workplace scenarios</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>40 communication approaches in 6 areas</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Personalized PDF report built from your answers</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Practical strategies, with parts written with AI help</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Your personal communication fingerprint</span>
                </li>
              </ul>
              <button
                onClick={handlePurchase}
                style={{...styles.pricingBtn, ...styles.pricingBtnSecondary}}
              >
                Get PASS Profile
              </button>
            </div>

            {/* Bundle Card */}
            <div style={{...styles.pricingCard, ...styles.pricingCardFeatured}}>
              <div style={styles.pricingBadge}>BEST VALUE &bull; SAVE $10</div>
              <h3 style={styles.pricingCardTitle}>PASS + Voice-Brand</h3>
              <div style={styles.pricingPrice}>$59.99</div>
              <p style={styles.pricingPriceNote}>One-time payment</p>
              <ul style={styles.pricingFeatureList}>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Everything in PASS Profile</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Voice-Brand Alignment report for your role</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Your favored approaches applied to your specific job</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Your professional communication brand</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>A 90-day plan that builds on approaches you already favor</span>
                </li>
              </ul>
              <button
                onClick={handleBundlePurchase}
                style={{...styles.pricingBtn, ...styles.pricingBtnPrimary}}
              >
                Get the Bundle
              </button>
            </div>

            {/* Course Bundle Card (off sale while the course is rewritten) */}
            {COURSE_ON_SALE && (
            <div style={styles.pricingCard}>
              <h3 style={styles.pricingCardTitle}>PASS + The Course</h3>
              <div style={styles.pricingPrice}>$59.99</div>
              <p style={styles.pricingPriceNote}>One-time payment</p>
              <ul style={styles.pricingFeatureList}>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Everything in PASS Profile</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Master Your PASS Profile: eight-module course</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Learn to read and use every part of your report</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Self-paced, with your progress saved</span>
                </li>
                <li style={styles.pricingFeatureItem}>
                  <span style={styles.pricingCheck}>&#10003;</span>
                  <span>Lifetime access</span>
                </li>
              </ul>
              <button
                onClick={handleCourseBundlePurchase}
                disabled={courseCheckoutLoading}
                style={{
                  ...styles.pricingBtn,
                  ...styles.pricingBtnSecondary,
                  ...(courseCheckoutLoading ? { opacity: 0.6, cursor: 'wait' } : {}),
                }}
              >
                {courseCheckoutLoading ? 'Starting checkout…' : 'Get PASS + Course'}
              </button>
              {courseCheckoutError && (
                <p style={styles.pricingError}>{courseCheckoutError}</p>
              )}
            </div>
            )}

          </div>
        </div>
      </section>

      {/* Go Deeper: Voice-Brand Alignment */}
      <section style={styles.goDeeper}>
        <div style={styles.goDeeperContainer}>
          <div style={styles.goDeeperContent}>
            <div style={styles.goDeeperLabel}>Included in the Bundle</div>
            <h2 style={styles.goDeeperTitle}>What Voice-Brand Alignment Adds</h2>
            <p style={styles.goDeeperText}>
              Your PASS Profile describes your communication fingerprint. Voice-Brand Alignment
              applies it to your specific role and shows how to use the approaches you favor with your
              audiences, in your industry.
            </p>
            <ul style={styles.goDeeperList}>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>Your professional communication brand statement</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>Your favored approaches matched to the people you communicate with</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>A role playbook for your most important work conversations</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>A 90-day plan that builds on approaches you already favor</span>
              </li>
            </ul>
            <p style={styles.goDeeperNote}>
              Voice-Brand Alignment is included in the $59.99 bundle above, or you can add it later from your portal.
            </p>
          </div>
          <div style={styles.goDeeperVisual}>
            <img src="/pass-voice-brand-bundle.png" alt="Voice-Brand Alignment Report" style={styles.goDeeperImage} />
          </div>
        </div>
      </section>

      {/* Go Deeper: Team Dynamics */}
      <section style={styles.teamDynamics}>
        <div style={styles.teamDynamicsContainer}>
          <div style={styles.goDeeperLabel}>Add-On</div>
          <h2 style={{...styles.goDeeperTitle, textAlign: 'center'}}>Team Dynamics Alignment</h2>
          <div style={styles.teamDynamicsContent}>
            <p style={{...styles.goDeeperText, textAlign: 'center'}}>
              See how your team communicates together. Team Dynamics compares the
              communication approaches favored by 2 to 12 team members and shows what your
              team shares, where it varies, how pairs can work together, and questions for
              a team conversation.
            </p>
            <ul style={{...styles.goDeeperList, maxWidth: '500px', margin: '0 auto 32px'}}>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>A team map of the approaches each member favored</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>Every team member receives their own copy by email</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>The approaches your team shares, and where it varies</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>How pairs on your team can combine their approaches</span>
              </li>
              <li style={styles.goDeeperListItem}>
                <span style={styles.goDeeperCheck}>&#10003;</span>
                <span>Working agreement prompts and conversation starters</span>
              </li>
            </ul>
          </div>
          <p style={{...styles.goDeeperNote, textAlign: 'center', marginBottom: '24px'}}>
            Prices below are in addition to each member's PASS Profile ($49.99 each).
          </p>
          <div style={styles.tierGrid}>
            <div style={styles.tierCard}>
              <div style={styles.tierName}>Pair</div>
              <div style={styles.tierSize}>2-3 people</div>
              <div style={styles.tierPrice}>$39.99</div>
            </div>
            <div style={styles.tierCard}>
              <div style={styles.tierName}>Team</div>
              <div style={styles.tierSize}>4-8 people</div>
              <div style={styles.tierPrice}>$79.99</div>
            </div>
            <div style={styles.tierCard}>
              <div style={styles.tierName}>Large</div>
              <div style={styles.tierSize}>9-12 people</div>
              <div style={styles.tierPrice}>$119.99</div>
            </div>
          </div>
          <a href="/login" style={styles.teamDynamicsCta}>
            Log In to Get Started
          </a>
          <p style={styles.teamDynamicsNote}>
            All team members must have a completed PASS Profile before purchasing. Add TDA from your portal after logging in.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section style={styles.finalCta}>
        <div style={styles.finalCtaContainer}>
          <h2 style={styles.finalCtaTitle}>Ready to Discover Your Communication Style?</h2>
          <p style={styles.finalCtaSubtitle}>
            Join professionals who have discovered the communication approaches they favor
            and started applying them with confidence.
          </p>
          <button onClick={scrollToPricing} style={styles.ctaButtonSecondary}>
            Get Your PASS Profile
          </button>
          <p style={styles.guarantee}>30-day money-back guarantee</p>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={styles.footerText}>© 2026 Ted's Voice Academy. All rights reserved.</p>
          <div style={styles.footerLinks}>
            <a href="/terms" style={styles.footerLink}>Terms of Service</a>
            <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
            <a href="mailto:PASS@tedsvoiceacademy.com" style={styles.footerLink}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
